import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  CheckCircle2, XCircle, RotateCcw, Clock, Eye, Cpu, Shield, 
  Loader2, ChevronDown, ChevronUp
} from "lucide-react";
import { API } from "@/constants";
import { VERIFICATION_STATUS_CONFIG } from "@/constants/legalSubjects";

const ICON_MAP = {
  Clock, Eye, Cpu, CheckCircle2, XCircle, RotateCcw, Shield
};

export const VerificationBadge = ({ status }) => {
  const config = VERIFICATION_STATUS_CONFIG[status] || VERIFICATION_STATUS_CONFIG.ai_generated;
  const Icon = ICON_MAP[config.icon] || Cpu;
  
  return (
    <Badge className={`${config.color} text-xs inline-flex items-center gap-1`} data-testid={`verification-badge-${status}`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

export const VerificationPanel = ({ itemId, itemType, currentStatus, onStatusChange }) => {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [history, setHistory] = useState([]);

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/verify/action`, {
        item_id: itemId,
        item_type: itemType,
        action,
        notes: notes || null,
      }, { withCredentials: true });
      
      if (res.data.status === "success") {
        onStatusChange?.(action);
        setNotes("");
      }
    } catch (e) {
      console.error("Verification error:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/verify/history`, {
        params: { item_id: itemId },
        withCredentials: true,
      });
      setHistory(res.data.verifications || []);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid="verification-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium">Lawyer Verification</span>
        </div>
        <VerificationBadge status={currentStatus || "ai_generated"} />
      </div>

      {currentStatus !== "approved" && (
        <>
          <Textarea
            placeholder="Add review notes (optional)..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mb-3 text-sm"
            rows={2}
            data-testid="verification-notes"
          />
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              onClick={() => handleAction("approved")}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="verify-approve-btn"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAction("revision_requested")}
              disabled={loading}
              className="border-orange-300 text-orange-700"
              data-testid="verify-revision-btn"
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Request Revision
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAction("rejected")}
              disabled={loading}
              className="border-red-300 text-red-700"
              data-testid="verify-reject-btn"
            >
              <XCircle className="h-3 w-3 mr-1" />
              Reject
            </Button>
          </div>
        </>
      )}

      {currentStatus === "approved" && (
        <p className="text-sm text-green-700 flex items-center gap-1">
          <CheckCircle2 className="h-4 w-4" />
          Verified and approved by responsible attorney
        </p>
      )}

      {/* History Toggle */}
      <button
        onClick={() => { setExpanded(!expanded); if (!expanded) loadHistory(); }}
        className="mt-3 text-xs text-blue-600 hover:underline flex items-center gap-1"
        data-testid="verification-history-toggle"
      >
        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        Verification History
      </button>
      
      {expanded && (
        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto">
          {history.length === 0 && <p className="text-xs text-slate-500">No verification history</p>}
          {history.map((h, i) => (
            <div key={i} className="text-xs border-l-2 border-slate-300 pl-3 py-1">
              <span className="font-medium">{h.verified_by_name}</span> — <VerificationBadge status={h.action} />
              {h.notes && <p className="text-slate-500 mt-0.5">{h.notes}</p>}
              <p className="text-slate-400">{new Date(h.verified_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ConfidenceIndicator = ({ score }) => {
  const level = score >= 91 ? { label: "Very High", color: "text-green-600", bg: "bg-green-500" }
    : score >= 71 ? { label: "High", color: "text-blue-600", bg: "bg-blue-500" }
    : score >= 41 ? { label: "Moderate", color: "text-yellow-600", bg: "bg-yellow-500" }
    : { label: "Low", color: "text-red-600", bg: "bg-red-500" };

  return (
    <div className="flex items-center gap-2" data-testid="confidence-indicator">
      <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full ${level.bg} rounded-full transition-all`} style={{ width: `${score}%` }} />
      </div>
      <span className={`text-xs font-medium ${level.color}`}>{score}% — {level.label}</span>
    </div>
  );
};
